import { Box } from '@mui/material';
import { useEffect, useState } from 'react';

import useResponsive from '@/components/hooks/useResponsive';
import HeroContent from '@/components/pages/visitorHome/VisitorHomeRebrandLayout/Hero/HeroContent';
import HeroZipPanel from '@/components/pages/visitorHome/VisitorHomeRebrandLayout/Hero/HeroZipPanel';

import heroImage from '@/../public/rebranding/hero-large.webp';
import heroImageMobile from '@/../public/rebranding/hero-mobile.webp';

type ImageType = {
  url: string;
  position?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
  };
};

const baseImageUrl = 'https://www.care.com/s/cms/web/vhp/hero';
const mobileImages: ImageType[] = [
  { url: heroImageMobile.src },
  { url: `${baseImageUrl}/hero-seniorcare-mobile.jpg?im=` },
  { url: `${baseImageUrl}/hero-pet-mobile.jpg?im=` },
  { url: `${baseImageUrl}/hero-housekeeping-mobile.jpg?im=` },
  { url: `${baseImageUrl}/hero-tutoring-mobile.jpg?im=` },
];
const desktopImages: ImageType[] = [
  {
    url: heroImage.src,
    position: {
      sm: '60%',
      md: 'center',
    },
  },
  { url: `${baseImageUrl}/hero-seniorcare-large.jpg?im=`, position: { sm: '34%', md: 'left' } },
  {
    url: `${baseImageUrl}/hero-pet-large.jpg?im=`,
    position: { sm: '61%', md: 'center' },
  },
  {
    url: `${baseImageUrl}/hero-housekeeping-large.jpg?im=`,
    position: { sm: '44%', lg: 'left' },
  },
  { url: `${baseImageUrl}/hero-tutoring-large.jpg?im=`, position: { sm: '39%', lg: 'left' } },
];

const preloadImages = (images: ImageType[]) => {
  images.forEach((image) => {
    const img = new Image();
    img.src = image.url;
  });
};

const HeroImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isMobile } = useResponsive();
  const carouselImages = isMobile ? mobileImages : desktopImages;

  useEffect(() => {
    preloadImages(carouselImages);
  }, [carouselImages]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        if (prevIndex === carouselImages.length - 1) {
          return 0;
        }
        return prevIndex + 1;
      });
    }, 5000);

    return () => clearInterval(timer); // Clear interval on unmount
  }, [carouselImages]);

  return (
    <Box data-testid="hero-image-carousel">
      <Box
        sx={() => ({
          position: 'relative',
          width: '100%',
          height: {
            xs: '246px',
            sm: '544px',
            md: '564px',
          },
          overflow: 'hidden',
          zIndex: 1,
        })}>
        {carouselImages.map((image, index) => (
          <Box
            key={`image-${image.url}`}
            sx={(theme) => ({
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: theme.palette.care?.white,
              backgroundImage: `url(${image.url})`,
              backgroundPosition: { xs: 'center', ...image?.position },
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: {
                xs: '0 0 70px 70px',
                sm: '0 0 120px 120px',
              },
              overflow: 'hidden',
              opacity: currentIndex === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
            })}
          />
        ))}
        <Box
          sx={(theme) => ({
            maxWidth: {
              xs: '100%',
              sm: '1280px',
            },
            padding: {
              xs: theme.spacing(11, 0, 0, 3),
              sm: theme.spacing(5, 0, 0, 4),
              md: theme.spacing(5, 0, 0, 8),
              lg: theme.spacing(5, 0, 0, 8),
              xl: theme.spacing(5, 0, 0, 0),
            },
            margin: '0 auto',
            position: 'relative',
          })}>
          <HeroContent />

          {!isMobile && <HeroZipPanel />}
        </Box>
      </Box>
      {isMobile && <HeroZipPanel />}
    </Box>
  );
};

export default HeroImageCarousel;
